<template>
  <v-flex>
    <import-dialog
      title="Import Market Data"
      uploadUrl="/api/report/marketData/import"
      :visible="showImportDialog"
      v-if="showImportDialog"
      @close="toggleImportDialog"
      @onSuccess="onSuccess"
    ></import-dialog>
    <page-toolbar>
      <kendo-dropdownlist
        :value="mdYear"
        @change="onMdYearChange"
        @databound="onMdYearDataBound"
        :dataSource="mdYearDataSource"
        data-text-field="code"
        data-value-field="periodYear"
        style="width:82px"
      ></kendo-dropdownlist>
      <kendo-datepicker
        :value="fromDate"
        @change="
          fromDate = $event.sender.value()
          read()
        "
        :min="minFromDate"
        :max="maxFromDate"
        :start="'year'"
        :depth="'year'"
        :format="'MMM-yy'"
        style="width:116px"
      >
      </kendo-datepicker>
      <kendo-datepicker
        :value="toDate"
        @change="
          toDate = $event.sender.value()
          read()
        "
        :min="minToDate"
        :max="maxToDate"
        :start="'year'"
        :depth="'year'"
        :format="'MMM-yy'"
        style="width:116px"
      >
      </kendo-datepicker>
      <kendo-dropdownlist
        :dataSource="marketDataSource"
        @databound="onMarketDataBound"
        :change="marketDrodownChange"
        data-text-field="marketCode"
        data-value-field="marketId"
        style="width:82px"
      ></kendo-dropdownlist>
      <visionap-switch-list
        :data-source="dataInstruments"
        :value="dataInstrumentsDataSource"
        data-value-field="dataInstrumentId"
        data-text-field="code"
        item-icon="file_download_done"
        :number-of-items="1"
        :show-select-all="true"
        select-all-label="Select all"
        width="232px"
        maxHeight="calc(100vh - 132px)"
        @input="dataInstruments = $event"
      ></visionap-switch-list>
      <kendo-button
        icon="add"
        title="Add new"
        v-if="!isReadOnly"
        v-show="validDates"
        @click="addRecord"
      >
      </kendo-button>
      <kendo-button
        type="button"
        title="Import data"
        v-if="!isReadOnly"
        @click="toggleImportDialog"
      >
        <v-icon small>archive</v-icon>
      </kendo-button>
      <kendo-button type="button" title="Open Chart" v-if="validDates" @click="showChart">
        <v-icon small>show_chart</v-icon>
      </kendo-button>
      <kendo-button type="button" title="Export to Excel" v-if="validDates" @click="exportToExcel">
        <i class="k-icon k-i-excel"></i>
      </kendo-button>
    </page-toolbar>
    <kendo-grid
      height="calc(100vh - 64px - 32px - 75px)"
      ref="marketDataGrid"
      :columns="columns"
      :dataSource="dataSource"
      :editable="!isReadOnly && 'inline'"
      :navigatable="true"
    ></kendo-grid>
  </v-flex>
</template>

<script>
import {mapGetters} from 'vuex'
import {numericEditor} from '../../helpers/kendo-editors'
import axios from 'axios'
import ImportDialog from '../../components/ImportDialog'
import marketDataChart from '../../dashboard-components/market-data-chart'
import PageToolbar from '@/components/PageToolbar'
import notification from '@/helpers/notification'
import security from '@/mixins/security'
import VisionapSwitchList from '@/components/VisionapSwitchList'

export default {
  name: 'MarketData',
  mixins: [security],
  computed: {
    dataSource() {
      if (!this.data) return
      return new kendo.data.DataSource({
        transport: {
          create: this.create,
          read: options => {
            options.success(this.data)
          },
          update: this.update,
        },
        schema: {
          model: {
            id: 'InterestRateId',
            fields: this.getDynamicFields(),
          },
        },
      })
    },
    columns() {
      const marketIr = '2'
      let marketId = this.marketId
      const mdColumns = this.mdColumns.map(column => ({
        ...column,
        width: 100,
        attributes: {style: 'text-align: right;'},
        template: function(e) {
          var value = e[column.interestRateCode] || 0
          if (marketId === marketIr) {
            return "<span style='cursor:cell;'>" + kendo.toString(value, 'p4') + '</span>'
          } else {
            return "<span style='cursor:cell;'>" + kendo.toString(value, 'n5') + '</span>'
          }
        },
        editor: numericEditor({
          format: marketId === marketIr ? 'p4' : 'n5',
          factor: marketId === marketIr ? 100 : 1,
          spinners: false,
          decimals: 16,
          step: 0,
        }),
      }))

      return [
        {
          command: [{name: 'edit'}],
          width: 82,
          attributes: {style: 'text-align: center;'},
          hidden: this.isReadOnly,
        },
        {
          field: 'EvaluationDate',
          title: 'Evaluation Date',
          visible: true,
          width: 110,
          format: '{0:yyyy-MM-dd}',
        },
        ...mdColumns,
        {},
      ]
    },
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({role: 'authentication/currentRole'}),
    ...mapGetters({tokenString: 'authentication/tokenString'}),
  },
  methods: {
    async getDataInstruments() {
      const {data: dataInstrumentsData} = await axios.get(
        `/api/report/marketData/getDataInstruments?marketId=${this.marketId}`,
        {},
      )
      this.dataInstrumentsDataSource = dataInstrumentsData
      const selectedValues = dataInstrumentsData.map(x => x.dataInstrumentId)
      this.dataInstruments = selectedValues
    },
    async create(e) {
      const data = this.getDataItem(e.data)

      await axios.post('/api/report/marketData', data).catch(({response}) => {
        e.error('Error', response.status, response.statusText)
      })
      e.success(true)
    },
    async read() {
      const grid = this.$refs.marketDataGrid.kendoWidget().element
      kendo.ui.progress(grid, true)
      const fromDate = this.fromDate
      const toDate = this.toDate
      if (!this.validDates || fromDate == null || toDate == null) {
        kendo.ui.progress(grid, false)
        return
      }

      const dataInstrumentIds = JSON.stringify(this.dataInstruments)
      const response = await axios.get('/api/report/marketData/getDataSource', {
        params: {fromDate, toDate, dataInstruments: dataInstrumentIds.toString()},
      })
      const {columns, data} = response.data

      this.mdColumns = columns
      this.data = data

      kendo.ui.progress(grid, false)
    },
    async update(e) {
      const data = this.getDataItem(e.data)

      await axios.put('/api/report/marketData', data).catch(({response}) => {
        e.error('Error', response.status, response.statusText)
      })
      e.success(true)
    },
    getDynamicFields() {
      const dynamicFields = this.mdColumns.reduce((acc, {field}) => {
        acc[field] = {
          field,
          editable: !this.isReadOnly,
          required: true,
          type: 'number',
        }
        return acc
      }, {})

      return {
        InterestRateId: {type: 'number', defaultValue: 0, editable: false},
        EvaluationDate: {type: 'date', editable: false},
        ...dynamicFields,
      }
    },
    getDataItem(data) {
      const interestRateId = data.InterestRateId
      const evaluationDate = kendo.toString(data.EvaluationDate, 'yyyy-MM-dd')
      const marketId = this.marketId
      const interestRateItems = this.mdColumns.map(column => ({
        interestRateCode: column.interestRateCode,
        value: data[column.field],
      }))

      return {
        interestRateId,
        marketId,
        evaluationDate,
        interestRateItems,
      }
    },
    addRecord() {
      const grid = this.$refs.marketDataGrid.kendoWidget()
      grid.addRow()
    },
    toggleImportDialog() {
      this.showImportDialog = !this.showImportDialog
    },
    onSuccess(e) {
      if (e.status == 0) {
        notification(e.message, 'info')
        this.read()
      } else {
        notification(e.message, 'error')
      }
    },
    exportToExcel() {
      const fromDate = kendo.toString(this.fromDate, 'yyyy-MM-dd')
      const toDate = kendo.toString(this.toDate, 'yyyy-MM-dd')
      const dataInstrumentIds = JSON.stringify(this.dataInstruments)
      const url = `/api/report/marketData/exportToExcel?marketId=${this.marketId}
      &fromDate=${fromDate}
      &toDate=${toDate}
      &dataInstruments=${dataInstrumentIds}
      &t=${this.tokenString}`
      location.href = url
    },
    onMarketDataBound(e) {
      this.marketId = e.sender.value()
      this.getDataInstruments()
    },
    marketDrodownChange(e) {
      this.marketId = e.sender.value()
      this.getDataInstruments()
    },
    onMdYearChange({sender}) {
      this.mdYear = sender.value()
      this.setDates(this.mdYear)
      this.read()
    },
    onMdYearDataBound({sender}) {
      const dataItem = sender.dataItem(0)
      if (dataItem) {
        this.mdYear = dataItem.periodYear
        this.setDates(this.mdYear)
        this.read()
      }
    },
    getLastMonth(year) {
      return new Date().getFullYear() == year ? new Date().getMonth() : 11
    },
    setDates(mdYear) {
      let minDate, maxDate
      if (!mdYear) return
      if (mdYear == 'All') {
        const years = this.mdYearDataSource.data().slice(0, -1)
        if (years.length >= 1) {
          this.validDates = true
          const [maxYear] = years
          let minYear = years[years.length - 1]
          if (!minYear) {
            minYear = maxYear
          }
          minDate = new Date(minYear.periodYear, 0, 1)
          maxDate = new Date(
            new Date(maxYear.periodYear, this.getLastMonth(maxYear.periodYear) + 1, 1).setDate(0),
          )
        } else {
          this.validDates = false
          notification('No available periods.', 'error')
          return
        }
      } else {
        minDate = new Date(mdYear, 0, 1)
        maxDate = new Date(new Date(mdYear, this.getLastMonth(mdYear) + 1, 1).setDate(0))
      }

      this.minFromDate = minDate
      this.maxFromDate = maxDate
      this.minToDate = minDate
      this.maxToDate = maxDate
      this.fromDate = minDate
      this.toDate = maxDate
    },
    showChart() {
      const fromDate = kendo.toString(this.fromDate, 'yyyy-MM-dd')
      const toDate = kendo.toString(this.toDate, 'yyyy-MM-dd')
      let options = {
        fromDate: fromDate,
        toDate: toDate,
        marketId: this.marketId,
        dataInstruments: this.dataInstruments,
      }

      marketDataChart.openChart(options)
    },
  },
  data() {
    return {
      mdYear: null,
      mdColumns: [],
      data: [],
      mdYearDataSource: new kendo.data.DataSource({
        schema: {
          parse(data) {
            return [...data, {periodYear: 'All', code: 'All'}]
          },
        },
        transport: {
          read: {
            url: () => `/api/report/marketData/getYears?clientId=${this.clientId}`,
            type: 'GET',
          },
        },
      }),
      validDates: true,
      fromDate: null,
      minFromDate: null,
      maxFromDate: null,
      toDate: null,
      minToDate: null,
      maxToDate: null,
      marketId: 1,
      marketDataSource: new kendo.data.DataSource({
        data: [
          {marketId: 1, marketCode: 'FX'},
          {marketId: 2, marketCode: 'IR'},
          {marketId: 3, marketCode: 'COM'},
        ],
      }),
      dataInstruments: [],
      dataInstrumentsDataSource: [],
      showImportDialog: false,
    }
  },
  watch: {
    async fromDate(fromDate) {
      if (!fromDate) return
      this.minToDate = new Date(fromDate)
    },
    async toDate(toDate) {
      if (!toDate) return
      this.maxFromDate = new Date(toDate)
    },
    async dataInstruments(dataInstruments) {
      if (!dataInstruments) return
      this.read()
    },
  },
  components: {
    ImportDialog,
    PageToolbar,
    VisionapSwitchList,
  },
}
</script>
